import type { Filter } from "@prisma/client";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import * as R from "ramda";
import type { MapData, OfferExt, OffersResult } from "src/types/offers";
import type { FiltersState } from "../offers/OffersSlice";
import {
  type PaginationState,
  createFiltersStateFromFilter,
} from "../offers/common";
import { serializeError } from "../storeUtils";

export const NON_REQUEST_DEALS_PARAMS: (keyof FiltersState)[] = ["view"];

export type FilterSort =
  | "greatOfferAtDesc"
  | "greatOfferAtAsc"
  | "discountDesc"
  | "discountAsc";

interface StateType {
  deals: OffersResult | null;
  dealsPending: boolean;
  mapData: MapData | null;
  filters: FiltersState;
  loadedFilters: FiltersState;
  pagination: PaginationState;
  error: string;
  filterMap: { [key: number]: Filter };
  nearby: {
    [key: number]: OfferExt[];
  };
}

const initialState: StateType = {
  deals: null,
  dealsPending: false,
  mapData: null,
  filters: {},
  loadedFilters: {},
  pagination: {},
  error: "",
  filterMap: {},
  nearby: {},
};

const DealsSlice = createSlice({
  name: "deals",
  initialState,
  reducers: {
    // HAS ERROR

    hasError(state: StateType, action) {
      state.error = serializeError(action.payload);
      state.dealsPending = false;
    },

    getDealsPending: (
      state,
      action: PayloadAction<{
        invalidatePreviousResults?: boolean;
        enableMarkers?: boolean;
      }>,
    ) => {
      state.dealsPending = true;
      if (action.payload.invalidatePreviousResults) {
        state.deals = null;
      }
    },
    getDealsResult: (state, action: PayloadAction<OffersResult>) => {
      state.deals = action.payload;
      if (action.payload.mapData !== undefined) {
        state.mapData = action.payload.mapData;
      }
      state.dealsPending = false;
      if (action.payload.filter) {
        state.filterMap[action.payload.filter.id] = action.payload.filter;

        const filters = createFiltersStateFromFilter(action.payload.filter);
        for (const param of NON_REQUEST_DEALS_PARAMS) {
          if (state.loadedFilters[param]) {
            (filters as any)[param] = state.loadedFilters[param];
          }
        }
        if (!R.equals(state.loadedFilters, filters)) {
          state.loadedFilters = filters;
        }
        if (!R.equals(state.filters, filters)) {
          state.filters = filters;
        }
      }
    },

    resetDealsResult: (state) => {
      state.deals = null;
      state.mapData = null;
      state.dealsPending = false;
    },

    filterUpdate(state, action: PayloadAction<FiltersState>) {
      if (Object.keys(action.payload).length === 0) {
        state.filters = {};
      } else {
        state.filters = R.mergeRight(state.filters, action.payload);
      }
    },

    loadedFiltersUpdate(state, action: PayloadAction<FiltersState>) {
      if (action.payload.filter) {
        const filterData = state.filterMap[action.payload.filter];
        if (filterData) {
          const filters = createFiltersStateFromFilter(filterData);
          for (const param of NON_REQUEST_DEALS_PARAMS) {
            if (action.payload[param]) {
              (filters as any)[param] = action.payload[param];
            }
          }
          if (!R.equals(state.loadedFilters, filters)) {
            state.loadedFilters = filters;
          }
          if (!R.equals(state.filters, filters)) {
            state.filters = filters;
          }
          return;
        }
      }

      if (!R.equals(state.loadedFilters, action.payload)) {
        state.loadedFilters = R.clone(action.payload);
      }
      if (!R.equals(state.filters, action.payload)) {
        state.filters = R.clone(action.payload);
      }
    },

    paginationUpdate(state, action: PayloadAction<PaginationState>) {
      if (!R.equals(state.pagination, action.payload)) {
        state.pagination = R.clone(action.payload);
      }
    },

    nearbyUpdate(
      state,
      action: PayloadAction<{ offerId: number; items: OfferExt[] }>,
    ) {
      state.nearby[action.payload.offerId] = action.payload.items;
    },
  },
});
export const {
  hasError,
  getDealsPending,
  getDealsResult,
  filterUpdate,
  loadedFiltersUpdate,
  paginationUpdate,
  resetDealsResult,
  nearbyUpdate,
} = DealsSlice.actions;

export default DealsSlice.reducer;
